import { memo } from "react";

import type { Column, Row, Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import { ScrollArea } from "@/components//ui/scroll-area";
import {
  Table as TableUI,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { DatasetAttributeSchema } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

interface TableDataProps<TData> {
  table: Table<TData>;
  schema: DatasetAttributeSchema[];
  highlightGeoColumns: DatasetAttributeSchema[];
  isHighlightNewGeometry: boolean;
  isHighlightedRow?: { rowId?: string; isHighlight?: boolean };
}

const defaultHeaderWidth = "w-[200px]";

function TableContent<TData>({
  rows,
  columns,
  isHighlightNewGeometry,
  highlightGeoColumns,
  isHighlightedRow
}: Readonly<{
  rows: Row<TData>[];
  columns: Column<TData, unknown>[];
  isHighlightNewGeometry: boolean;
  highlightGeoColumns: DatasetAttributeSchema[];
  isHighlightedRow?: { rowId?: string; isHighlight?: boolean };
}>) {
  return (
    <TableBody>
      {rows?.length ? (
        rows.map((row, rowIndex) => (
          <TableRow
            key={row.id ?? `row-${rowIndex}`}
            data-state={row.getIsSelected() && "selected"}
            data-testid={`table-${row.id}`}
            className={twMerge(
              isHighlightedRow?.rowId === row.id.toString() &&
                isHighlightedRow?.isHighlight &&
                "bg-teal-50"
            )}
          >
            {row.getVisibleCells().map((cell) => {
              const content = flexRender(
                cell.column.columnDef.cell,
                cell.getContext()
              );
              const isLastRow = rowIndex === rows.length - 1;
              const isFirstCell = row.getVisibleCells()[0]?.id === cell.id;
              const isLastCell =
                row.getVisibleCells()[row.getVisibleCells().length - 1]?.id ===
                cell.id;
              const isPinned = cell.column.getIsPinned();
              const isHidden = cell.column.getCanHide();
              // Determine if the value is empty
              const value = cell.getValue();
              const isEmpty =
                value === null ||
                (value === undefined && cell.column.id !== "action") ||
                value === "" ||
                (typeof value === "number" && Number.isNaN(value)) ||
                (typeof value === "object" &&
                  value !== null &&
                  Object.keys(value).length === 0);

              return (
                !isHidden && (
                  <TableCell
                    key={cell.id ?? `cell-${rowIndex}-${cell.column.id}`}
                    className={twMerge(
                      "py-2 px-3 !border-b",
                      isFirstCell && "!border-l",
                      isLastCell && "!border-r",
                      "p-0 max-w-[200px] py-2 px-3",
                      isLastRow && isFirstCell && "rounded-bl-lg",
                      isLastRow && isLastCell && "rounded-br-lg",
                      isEmpty && "bg-white-smoke",
                      isHighlightNewGeometry &&
                        highlightGeoColumns?.some(
                          (col) => col.attribute_name === cell.column.id
                        ) &&
                        "bg-teal-100",
                      isPinned &&
                        "sticky right-0 !border-l bg-white shadow-[-15px_0px_38px_-22px_rgba(0,0,0,0.75)]"
                    )}
                    aria-label={cell.column.id}
                  >
                    {content}
                  </TableCell>
                )
              );
            })}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={columns.length} className="h-24 text-center">
            No results.
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

const MemoizedTableContent = memo(TableContent) as typeof TableContent;

function TableData<TData>({
  table,
  schema,
  highlightGeoColumns,
  isHighlightNewGeometry,
  isHighlightedRow
}: Readonly<TableDataProps<TData>>) {
  return (
    <ScrollArea className="h-[calc(100vh-21rem)]">
      <TableUI className="relative table-fixed border-separate border-spacing-0">
        <TableHeader className="z-50">
          {table.getHeaderGroups().map((headerGroup, index) => (
            <TableRow key={headerGroup.id ?? `header-${index}`}>
              {headerGroup.headers.map((header, index) => {
                const isPinned = header.column.getIsPinned();
                const isHidden = header.column.getCanHide();
                return (
                  !isHidden && (
                    <TableHead
                      data-testid={`table-head-${header.id}`}
                      key={`${header.id}_${header.index}`} // Use header.index for unique key as header.id could be duplicated e.g. when multiple columns use the same attribute name
                      className={twMerge(
                        "h-14 bg-white-smoke py-2 px-3 !border-t !border-b !border-r",
                        defaultHeaderWidth,
                        isHighlightNewGeometry &&
                          highlightGeoColumns?.some(
                            (col) =>
                              col.attribute_name ===
                              schema?.[index]?.attribute_name
                          ) &&
                          "bg-teal-100",
                        schema?.[index]?.is_reference_id && "bg-neutral-200",
                        header.index === 0 && "!border-l rounded-tl-lg",
                        header.index === headerGroup.headers.length - 1 &&
                          "rounded-tr-lg",
                        isPinned && "sticky top-0 right-0 !border-l"
                      )}
                      aria-label={schema?.[index]?.attribute_name}
                      style={{
                        width:
                          // using the maxSize logic from react-table
                          header.column.columnDef.maxSize ===
                          Number.MAX_SAFE_INTEGER
                            ? undefined
                            : `${header.getSize()}px`
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <MemoizedTableContent
          rows={table.getRowModel().rows}
          columns={table.getAllColumns()}
          isHighlightNewGeometry={isHighlightNewGeometry}
          highlightGeoColumns={highlightGeoColumns}
          isHighlightedRow={isHighlightedRow}
        />
      </TableUI>
    </ScrollArea>
  );
}

export default TableData;
